import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Crown, ArrowRight, Loader2, Mail, Users } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const COLORS = {
  royalBlue: '#002366',
  oxblood: '#800000',
  parchment: '#F5F5DC',
  gold: '#D4AF37'
};

const LChaimSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    verifySubscription();
  }, []);

  const verifySubscription = async () => {
    try {
      const sessionId = searchParams.get('session_id');
      const token = localStorage.getItem('academy_token');
      
      if (!token) {
        navigate('/academy/lchaim/login');
        return;
      }

      // Get user info
      const userRes = await api.get('/academy/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (userRes.data.success) {
        setUser(userRes.data.user);
      }

      // Verify the checkout session if we have a session ID
      if (sessionId) {
        const verifyRes = await api.post('/academy/lchaim/verify-subscription', {
          session_id: sessionId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (verifyRes.data.success) {
          setSubscription(verifyRes.data.subscription);
        }
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.parchment }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: COLORS.royalBlue }} />
          <p className="font-serif" style={{ color: COLORS.oxblood }}>Confirming your enrollment...</p>
        </div>
      </div>
    );
  }

  const tierName = subscription?.tier_name || 'L\'Chaim';
  const isTandem = subscription?.tier === 'lchaim_order';
  const hasDashboard = subscription?.tier !== 'lchaim_sentry';

  return (
    <div className="min-h-screen py-12 px-6" style={{ backgroundColor: COLORS.parchment }}>
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div 
            className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: COLORS.royalBlue }}
          >
            <Check className="w-12 h-12" style={{ color: COLORS.gold }} />
          </div>
          
          <img 
            src="/lchaim-logo.png" 
            alt="L'Chaim" 
            className="h-12 mx-auto mb-4"
          />
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
            Welcome to The Forge
          </h1>
          <p className="font-serif text-lg" style={{ color: COLORS.oxblood }}>
            Your 350-day transformation begins now
          </p>
        </div>

        {/* Subscription Details */}
        <Card className="mb-8" style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
          <CardContent className="p-8">
            <div className="flex items-center gap-4 mb-6 pb-6" style={{ borderBottom: `2px solid ${COLORS.parchment}` }}>
              <Crown className="w-10 h-10" style={{ color: COLORS.gold }} />
              <div>
                <h2 className="text-xl font-serif font-bold" style={{ color: COLORS.royalBlue }}>
                  {tierName}
                </h2>
                <p className="font-serif text-sm" style={{ color: COLORS.oxblood }}>
                  {user?.email}
                </p>
              </div>
            </div>

            <h3 className="font-serif font-bold mb-4" style={{ color: COLORS.royalBlue }}>
              What's Next:
            </h3>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.gold }} />
                <div>
                  <p className="font-serif font-bold" style={{ color: COLORS.royalBlue }}>Day 1 Content Ready</p>
                  <p className="font-serif text-sm" style={{ color: COLORS.oxblood }}>
                    Your first daily Order is waiting. Start with the Pillar of Respect.
                  </p>
                </div>
              </li>

              {hasDashboard && (
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.gold }} />
                  <div>
                    <p className="font-serif font-bold" style={{ color: COLORS.royalBlue }}>Dashboard Access</p>
                    <p className="font-serif text-sm" style={{ color: COLORS.oxblood }}>
                      Use the Sentry's Log for journaling and the Ancestral Vault for uploads.
                    </p>
                  </div>
                </li>
              )}

              {isTandem && (
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.gold }} />
                  <div>
                    <p className="font-serif font-bold" style={{ color: COLORS.royalBlue }}>Link Your Son's Account</p>
                    <p className="font-serif text-sm" style={{ color: COLORS.oxblood }}>
                      Connect with your son to access the Coach's Companion and sync progress.
                    </p>
                  </div>
                </li>
              )}

              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: COLORS.gold }} />
                <div>
                  <p className="font-serif font-bold" style={{ color: COLORS.royalBlue }}>
                    {hasDashboard ? 'Daily Emails' : 'Daily Email Delivery'}
                  </p>
                  <p className="font-serif text-sm" style={{ color: COLORS.oxblood }}>
                    Check your inbox each morning for your daily Order.
                  </p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          {hasDashboard ? (
            <Button 
              onClick={() => navigate('/academy/lchaim/dashboard')}
              className="w-full py-6 font-serif font-bold text-lg"
              style={{ backgroundColor: COLORS.oxblood, color: COLORS.parchment }}
            >
              Enter The Forge <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/academy/lchaim')}
              className="w-full py-6 font-serif font-bold text-lg"
              style={{ backgroundColor: COLORS.oxblood, color: COLORS.parchment }}
            >
              <Mail className="w-5 h-5 mr-2" /> Check Your Email
            </Button>
          )}

          {isTandem && (
            <Button 
              onClick={() => navigate('/academy/lchaim/link-account')}
              variant="outline"
              className="w-full py-6 font-serif font-bold"
              style={{ borderColor: COLORS.royalBlue, color: COLORS.royalBlue }}
            >
              <Users className="w-5 h-5 mr-2" /> Link Your Son's Account
            </Button>
          )}
        </div>

        {/* Footer Message */}
        <div className="text-center mt-12 p-6 rounded-lg" style={{ backgroundColor: COLORS.royalBlue }}>
          <p className="font-serif italic" style={{ color: COLORS.parchment }}>
            "A boy doesn't become a man by accident. He is forged by intention."
          </p>
          <p className="font-serif text-sm mt-2" style={{ color: COLORS.gold }}>
            — L'Chaim: The 13th Year Forge
          </p>
        </div>
      </div>
    </div>
  );
};

export default LChaimSuccess;
